/* eslint-disable */
// @ts-nocheck
import B from 'bluebird';
import * as R from 'ramda';
import { TABLE_TO_TOTALS_MAPPING } from '../../../../shared/constants/analytics/contexts';
import dash_api from '../../main/support/dash_api';

import { ATTRIBUTED, LAST_ATTRIBUTED_TOUCH_TYPE } from '../../../../shared/constants/cenariusDimensions';
import { TOUCH_TYPE } from '../../../../shared/constants/touchTypes';
import {
  ReduxAsyncAction,
  serializeWithSort,
  generateConstants,
  DEFAULT_DRUID_RECORD_LIMIT,
  getWhitelistedErrorMessage,
  getEoDatasource,
  mapToEventName,
} from '../support/utils';
import { ISO_DATE_ANALYTICS } from '../../main/support/eo-transition-constants';
import {
  ANALYTICS_REPORT_ENDPOINT_BY_CONTEXT,
  ANALYTICS_TRENDS_ENDPOINT_BY_CONTEXT,
  ANALYTICS_REPORT_TOTALS_ENDPOINT_BY_CONTEXT,
} from '../../main/constants';
import { ERROR_WHITELIST } from '../support/analyticsConstants';
import { DATASOURCES, ATTRIBUTED_TOUCH_DATASOURCES, DATASOURCES_WITH_ALIASES } from '../support/eo_mapping';
import { EO_CLICK, EO_IMPRESSION } from '../support/eo_mapping/datasources';
import {
  getRangeInTimezoneEndingNow,
  timezoneIsGuessed,
  appTimezone,
  formatDateRange,
  WITH_OFFSET_TIMESTAMP_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '../../main/support/dateUtils';
import { CALCULTED_DATASOURCES_TO_BASIC_DATASOURCES } from '../support/eo_mapping/calculated_properties';

import type { RequestBody } from '../../main/types/druidTypes';
import InFilter from '../support/utils/druid/filters/InFilter';
import { EO_CUSTOM_EVENT } from '../support/eo_mapping/datasources';
import { ErrorProvider } from '../../../libraries/branch-tooling-next/Error/ErrorProvider';

export const TIME_SERIES = 'timeseries';
export const TOP_N = 'topn';
export const GROUP_BY = 'groupby';
export const FILTERS = 'filters';
export const COHORTS = 'cohorts';

const VALID_TYPES = [TIME_SERIES, TOP_N, GROUP_BY, FILTERS, COHORTS];

const DEFAULT_DURATION_FOR_EVENTS = 30;

const SHORTER_DEFAULT_DURATION_FOR_EVENTS = 7;

const DEFAULT_FILTERS_RANGE = 7;
const SHORT_DEFAULT_FILTERS_RANGE = 3;

let apiLimit = undefined;

const errorProvider = new ErrorProvider();

const translateErrorMap = {
  'This query has exceeded max limit set': 'Time range request limit exceeded. Please try again in 2 minutes.',
  'Malformed request: This query has exceeded max filters quantity':
    'Too many filter values have been selected. Please reduce the number of selected filter values and try again.',
  'Dimension conflict':
    "It is not possible to compare by this combination of dimensions due to limitations of the ad partner's API.",
  'Error fetching cost data from zeit':
    'Your data is taking too long to load. Please reload the page, reduce your time period, or try again later.',
  'Access To Data Restricted': 'Access to data restricted. Please contact app admin to request access.',
  'Access To Revenue Data Restricted': 'Access to revenue data restricted. Please contact app admin to request access.',
  'Error Requesting Analytics Data': 'Error Requesting Analytics Data',
};

function isFlipper(name) {
  return window.App.context.flipper && window.App.context.flipper[name];
}

function getDefaultDruidDuration() {
  if (isFlipper('shorter_default_druid_query_duration')) {
    return SHORTER_DEFAULT_DURATION_FOR_EVENTS;
  }
  return DEFAULT_DURATION_FOR_EVENTS;
}

function getDefaultDruidFilterRange() {
  if (isFlipper('reduced_default_filter_range_druid')) {
    return SHORT_DEFAULT_FILTERS_RANGE;
  }
  return DEFAULT_FILTERS_RANGE;
}

function isLimitReached(limit, data) {
  // for cohorts since data comes back as an Array rather than an Object
  if (Array.isArray(data)) {
    return data.length >= limit;
  }

  return Object.keys(data).reduce((acc, curr) => {
    if (data[curr] && data[curr].length >= limit) {
      acc = true;
    }
    return acc;
  }, false);
}

function getErrorMessage(message) {
  try {
    return JSON.parse(message)
      .errors.reduce((acc, error) => {
        if (!translateErrorMap[error]) {
          return acc;
        }
        return R.append(error, acc);
      }, [])
      .join(', ');
  } catch (e) {}

  if (!message) {
    return null;
  }

  if (translateErrorMap[message]) {
    return translateErrorMap[message];
  }

  if (getWhitelistedErrorMessage({ error: message, whitelist: ERROR_WHITELIST })) {
    return message;
  }

  return null;
}

const GetDruidLimit = new ReduxAsyncAction('GET_DRUID_LIMIT');
GetDruidLimit.registerRequest(async () => {
  if (apiLimit === undefined) {
    apiLimit = await dash_api.get('/v1/druid/limit');
    apiLimit = !apiLimit.data ? 0 : apiLimit.data;
  }

  return apiLimit;
});

export const GET_DRUID_LIMIT = GetDruidLimit.constants;
export const getDruidLimit = GetDruidLimit.request;

const FetchReportTotals = new ReduxAsyncAction('FETCH_REPORT_TOTALS');
FetchReportTotals.registerRequest(async ({ totals, reportQuery, ...hashData }) => {
  const response = { ...hashData };

  if (totals) {
    response.totals = totals;
    return response;
  }

  const analyticsReportTotalsEndpoint = ANALYTICS_REPORT_TOTALS_ENDPOINT_BY_CONTEXT[reportQuery.context];

  if (!analyticsReportTotalsEndpoint) {
    response.error = `Analytics report totals not available for: ${reportQuery.context}.`;
    return response;
  }

  const analyticsReportTotalsContext = TABLE_TO_TOTALS_MAPPING[reportQuery.context] || reportQuery.context;

  try {
    const totals = await dash_api.post(analyticsReportTotalsEndpoint, {
      start_date: reportQuery.start_date,
      end_date: reportQuery.end_date,
      filter: reportQuery.filter,
      columns: reportQuery.columns,
      organic: reportQuery.organic,
      context: analyticsReportTotalsContext,
    });
    response.totals = totals?.data;
  } catch (error) {
    response.error = errorProvider.parse(error).getMessageOrEmpty() || 'Error requesting report totals. Please try again.';
  }

  if (!response.totals && !response.error) {
    response.error = 'Error requesting report totals. Please try again.';
  }

  return response;
});

export const FETCH_REPORT_TOTALS = FetchReportTotals.constants;
export const fetchReportTotals = FetchReportTotals.request;

const DOMAIN_ANALYTICS_ENDPOINT_BY_CONTEXT = {
  ...ANALYTICS_REPORT_ENDPOINT_BY_CONTEXT,
  ...ANALYTICS_TRENDS_ENDPOINT_BY_CONTEXT,
};

export const isDomainAnalyticsAvailable = (context) => !!DOMAIN_ANALYTICS_ENDPOINT_BY_CONTEXT[context];

export const GET_DRUID_DATA = generateConstants('GET_DRUID_DATA');

export function getDruidData(type: string, body: RequestBody) {
  return async function gettingDruidData(dispatch: (action: Object) => Object) {
    const { query = {}, sourceQuery, overrides, reportQuery, trendsQuery } = body;

    const isReportQuery = !!reportQuery;
    const isTrendsQuery = !!trendsQuery;
    delete body.reportQuery;
    delete body.trendsQuery;

    const baseResponse = {
      query,
      sourceQuery,
      overrides,
      type,
      isReportQuery,
      isTrendsQuery,
    };

    dispatch({
      type: GET_DRUID_DATA.request,
      data: { ...baseResponse },
    });

    const responseHandler = (data) => {
      dispatch({
        type: GET_DRUID_DATA.response,
        data,
      });
    };

    const errorHandler = (error) => {
      console.log('error', error);
      // default error message
      const message = errorProvider.parse(error).getMessageOrEmpty();
      const status = errorProvider.parse(error).getStatus();
      const errorMsg = getErrorMessage(message) || 'Error requesting data. Please try again.';

      dispatch({
        error: errorMsg,
        type: GET_DRUID_DATA.error,
        data: { ...baseResponse },
        lastRateLimitHit: status === 429 ? Date.now() : undefined,
      });
    };

    const res = { ...baseResponse };

    const executeReportQuery = async () => {
      const analyticsReportEndpoint = DOMAIN_ANALYTICS_ENDPOINT_BY_CONTEXT[reportQuery.context];
      if (!analyticsReportEndpoint) {
        const message = `Analytics Reporting not available for: ${reportQuery.context}.`;
        return errorHandler({ data: { message } });
      }

      if (!reportQuery?.columns?.length) {
        res.results = [];
        return responseHandler(res);
      }

      reportQuery.metric = query.metric;
      reportQuery.organic = body.organic;

      try {
        const reportResults = await dash_api.post(analyticsReportEndpoint, reportQuery);
        const { data, limitedResults, movedPlatformValues, totals } = reportResults?.data || {};
        res.results = data;
        res.limitedResults = limitedResults;
        res.movedPlatformValues = movedPlatformValues;
        responseHandler(res);
        dispatch(fetchReportTotals({ totals, reportQuery, ...baseResponse }));
      } catch (error) {
        return errorHandler(error);
      }
    };

    const executeTrendsQuery = async () => {
      const analyticsTrendsEndpoint = DOMAIN_ANALYTICS_ENDPOINT_BY_CONTEXT[trendsQuery.context];
      if (!analyticsTrendsEndpoint) {
        const message = `Analytics Trends not available for: ${trendsQuery.context}.`;
        return errorHandler({ data: { message } });
      }

      if (!trendsQuery?.topics?.length) {
        res.results = [];
        return responseHandler(res);
      }

      trendsQuery.metric = query.metric || 'total_count';
      trendsQuery.organic = body.organic || false;

      try {
        const trendsResults = await dash_api.post(analyticsTrendsEndpoint, trendsQuery);
        const { data = {} } = trendsResults;
        res.results = data;
        responseHandler(res);
      } catch (error) {
        return errorHandler(error);
      }
    };

    if (isReportQuery) {
      await executeReportQuery();
    } else if (isTrendsQuery) {
      await executeTrendsQuery();
    } else {
      body.app_time_zone = appTimezone;
      body.app_time_zone_is_guessed = timezoneIsGuessed;

      // validate
      if (!VALID_TYPES.includes(type)) {
        throw `${type} is not a valid type`;
      }

      if (apiLimit === undefined) {
        try {
          apiLimit = await dash_api.get('/v1/druid/limit');
          apiLimit = !apiLimit.data ? 0 : apiLimit.data;
          dispatch({ type: GET_DRUID_LIMIT.response, data: apiLimit });
        } catch (e) {}
      }

      try {
        const route = type === 'cohorts' ? '/v2/analytics/cohorts' : `/v1/druid/${type}`;

        // make request
        const { data } = await dash_api.post(route, body);

        // when 'attributed' is a dimension:
        // 1) add attributed='true' to each "last attributed touch topic"
        // 2) change attributed='false' to attributed='false (organic)' when false (done by formatter function)
        if (query.dimensions && R.contains(ATTRIBUTED, query.dimensions)) {
          Object.keys(data).map((source) => {
            if (R.contains(source, ATTRIBUTED_TOUCH_DATASOURCES)) {
              data[source] = data[source].map((r) => {
                if (r.event) {
                  r.event.attributed = 'true';
                }
                return r;
              });
            }
          });
        }

        if (query.dimensions && R.contains(LAST_ATTRIBUTED_TOUCH_TYPE, query.dimensions)) {
          Object.keys(data).map((dataSource) => {
            if (dataSource === EO_CLICK) {
              data[dataSource].forEach((r) => {
                if (r.event) {
                  r.event.last_attributed_touch_type = TOUCH_TYPE.CLICK;
                }
              });
            } else if (dataSource === EO_IMPRESSION) {
              data[dataSource].forEach((r) => {
                if (r.event) {
                  r.event.last_attributed_touch_type = TOUCH_TYPE.IMPRESSION;
                }
              });
            }
          });
        }

        res.results = data;
      } catch (error) {
        return errorHandler(error);
      }

      res.limit = query.limit || apiLimit || DEFAULT_DRUID_RECORD_LIMIT;
      res.limitReached = isLimitReached(res.limit, res.results);

      responseHandler(res);
    }
  };
}

const default_date_range = getRangeInTimezoneEndingNow(getDefaultDruidFilterRange());

const filterCache = {};

const GetDruidFiltersByDimension = new ReduxAsyncAction('GET_DRUID_FILTERS');
GetDruidFiltersByDimension.registerRequest(function (
  date_range: any | undefined | null,
  dimensions: string[],
  data_sources: Array<string> = R.keys(DATASOURCES)
) {
  // format start and end date as YYYY-MM-DDTHH:mm:ss since this endpoint queries druid directly (not cenarius).
  date_range = date_range ? formatDateRange(date_range, WITH_OFFSET_TIMESTAMP_FORMAT) : default_date_range;

  const serialized = serializeWithSort({
    date_range,
    dimensions,
    data_sources,
  });
  if (filterCache[serialized]) {
    return B.resolve(filterCache[serialized]);
  }

  // for calculated datasources (non cohort), need to map to a basic datasource (e.g. eCPI => eo_install) when fetching filters since cenarius has no conception of calculated datasources.
  data_sources = R.uniq(
    data_sources.filter((ds) => ds.includes('cohort')).length > 0
      ? data_sources
      : data_sources.reduce((acc, ds) => {
          const basicDs = CALCULTED_DATASOURCES_TO_BASIC_DATASOURCES[ds];
          if (!basicDs) {
            return [...acc, ds];
          }
          return [...acc, ...basicDs];
        }, [])
  );

  const body = {
    dimensions,
    query: {
      data_sources,
      start_date: date_range.start_date,
      end_date: date_range.end_date,
    },
    app_time_zone: appTimezone,
  };

  return dash_api.post(`/v1/druid/filters`, body).then(({ data }) => {
    filterCache[serialized] = data;
    return dimensions.reduce((preprocessedResponse, currentValue) => {
      return { ...preprocessedResponse, [currentValue]: data[currentValue] || [] };
    }, {});
  });
});

export const GET_DRUID_FILTERS = GetDruidFiltersByDimension.constants;
export const getDruidFiltersByDimension = GetDruidFiltersByDimension.request;

const getDruidEvents =
  (data_sources: Array<string>, provideAliases?: boolean = false) =>
  () => {
    const date_range = getRangeInTimezoneEndingNow(getDefaultDruidDuration(), DEFAULT_DATE_FORMAT);

    // Reason for using groupby query:
    // For certain datasources we append the name dimension to the dimension list.
    // This happens in the dashboard server
    const dimensions = provideAliases ? ['customer_event_alias'] : 'name';
    const endpoint = `/v1/druid/${provideAliases ? 'groupby' : 'topn'}`;
    const query = {
      start_date: date_range.start_date,
      end_date: date_range.end_date,
      context: 'druid-events',
      data_sources,
      [provideAliases ? 'dimensions' : 'dimension']: dimensions,
      eo_cutoff_date: ISO_DATE_ANALYTICS,
    };

    return dash_api.post(endpoint, { query, overrides: { disableReferredOnly: true } }).then(R.prop('data'));
  };

const getTopDruidEventsByName = (data_sources: Array<string>) => () => {
  const date_range = getRangeInTimezoneEndingNow(getDefaultDruidDuration(), DEFAULT_DATE_FORMAT);

  const query = {
    start_date: date_range.start_date,
    end_date: date_range.end_date,
    context: 'druid-events',
    data_sources,
    dimension: 'name',
    eo_cutoff_date: ISO_DATE_ANALYTICS,
  };

  return dash_api.post('/v1/druid/topn', { query, overrides: { disableReferredOnly: true } }).then(R.prop('data'));
};

const GetCustomDruidEvents = new ReduxAsyncAction('GET_CUSTOM_DRUID_EVENTS');
GetCustomDruidEvents.registerRequest(getDruidEvents(['eo_custom_event']));

export const GET_CUSTOM_DRUID_EVENTS = GetCustomDruidEvents.constants;
export const getCustomDruidEvents = GetCustomDruidEvents.request;

const GetAllDruidEvents = new ReduxAsyncAction('GET_ALL_DRUID_EVENTS');
GetAllDruidEvents.registerRequest(getDruidEvents(['eo_custom_event'], true));

export const GET_ALL_DRUID_EVENTS = GetAllDruidEvents.constants;
export const getAllDruidEvents = GetAllDruidEvents.request;

const GetAllTopDruidEventsByName = new ReduxAsyncAction('GET_ALL_TOP_DRUID_EVENTS_BY_NAME');
GetAllTopDruidEventsByName.registerRequest(getTopDruidEventsByName(['eo_custom_event']));

export const GET_ALL_TOP_DRUID_EVENTS_BY_NAME = GetAllTopDruidEventsByName.constants;
export const getAllTopDruidEventsByName = GetAllTopDruidEventsByName.request;

const GetTopDruidCustomerEventAliasesByName = new ReduxAsyncAction('GET_TOP_DRUID_CUSTOMER_EVENT_ALIASES_BY_NAME');
GetTopDruidCustomerEventAliasesByName.registerRequest((eventName: string) => {
  // DASH-3035: event mapping only references non-eo event names, so the default
  // keeps the eoDatasource values accurate.
  const eoDatasource = getEoDatasource(eventName) || EO_CUSTOM_EVENT;
  if (!DATASOURCES_WITH_ALIASES.includes(eoDatasource)) {
    return Promise.resolve({
      data: { [eoDatasource]: [] },
      eventName,
      datasource: eoDatasource,
    });
  }
  const date_range = getRangeInTimezoneEndingNow(getDefaultDruidDuration(), DEFAULT_DATE_FORMAT);

  const query = {
    start_date: date_range.start_date,
    end_date: date_range.end_date,
    context: 'druid-events',
    data_sources: [eoDatasource],
    dimension: 'customer_event_alias',
    eo_cutoff_date: ISO_DATE_ANALYTICS,
    filter: new InFilter('name', [mapToEventName(eventName)]),
  };

  return dash_api.post('/v1/druid/topn', { query, overrides: { disableReferredOnly: true } }).then((res) => ({
    data: res.data,
    eventName,
    datasource: eoDatasource,
  }));
});
export const GET_TOP_DRUID_CUSTOMER_EVENT_ALIASES_BY_NAME = GetTopDruidCustomerEventAliasesByName.constants;
export const getTopDruidCustomerEventAliasesByName = GetTopDruidCustomerEventAliasesByName.request;

const GetAllTopDruidCustomerEventAliases = new ReduxAsyncAction('GET_ALL_TOP_DRUID_CUSTOMER_EVENT_ALIASES');
GetAllTopDruidCustomerEventAliases.registerRequest(() => {
  const date_range = getRangeInTimezoneEndingNow(getDefaultDruidDuration(), DEFAULT_DATE_FORMAT);

  const query = {
    start_date: date_range.start_date,
    end_date: date_range.end_date,
    context: 'druid-events',
    data_sources: DATASOURCES_WITH_ALIASES,
    dimension: 'customer_event_alias',
    eo_cutoff_date: ISO_DATE_ANALYTICS,
  };

  return dash_api.post('/v1/druid/topn', { query, overrides: { disableReferredOnly: true } }).then((res) => res.data);
});

export const GET_ALL_TOP_DRUID_CUSTOMER_EVENT_ALIASES = GetAllTopDruidCustomerEventAliases.constants;
export const getAllTopDruidCustomerEventAliases = GetAllTopDruidCustomerEventAliases.request;

export const RESET_LAST_RATE_LIMIT_HIT = 'RESET_LAST_RATE_LIMIT_HIT';
export const resetDruidRateLimit = () => {
  return {
    type: RESET_LAST_RATE_LIMIT_HIT,
  };
};
